import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Check, XCircle, MessageSquare, User, Bot } from 'lucide-react'
import { api } from '../services/api'
import { AiMessageReport, Message } from '../types/ai-reports'

interface AiMessageReportDetailsModalProps {
  report: AiMessageReport
  onClose: () => void
}

export function AiMessageReportDetailsModal({ report, onClose }: AiMessageReportDetailsModalProps) {
  const [selectedStatus, setSelectedStatus] = useState<AiMessageReport['status']>(report.status)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const queryClient = useQueryClient()

  // Parse messages context
  let messages: Message[] = []
  try {
    messages = JSON.parse(report.messages_context)
  } catch (e) {
    console.error('Failed to parse messages context:', e)
  }

  const updateStatusMutation = useMutation({
    mutationFn: async ({ reportId, status }: { reportId: string; status: AiMessageReport['status'] }) => {
      await api.patch(`/admin/ai-reports/${reportId}/status`, {
        status
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-reports'] })
      queryClient.invalidateQueries({ queryKey: ['ai-report-details', report.id] })
      setShowUpdateModal(false)
      onClose()
    },
  })

  const handleStatusChange = (newStatus: AiMessageReport['status']) => {
    setSelectedStatus(newStatus)
    setShowUpdateModal(true)
  }

  const handleConfirmStatusUpdate = () => {
    updateStatusMutation.mutate({
      reportId: report.id,
      status: selectedStatus
    })
  }

  const formatDate = (dateString: string | Date | number | undefined) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  const statusColors = {
    open: 'text-yellow-600 bg-yellow-50',
    resolved: 'text-green-600 bg-green-50',
    ignored: 'text-blue-600 bg-blue-50',
  }

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
          </div>

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2 text-red-600" />
                  AI Message Report Details
                </h3>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Report Info */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">User ID</label>
                      <p className="mt-1 text-sm text-gray-900">{report.user_id}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[report.status]}`}>
                          {report.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Created At</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(report.created_at)}</p>
                    </div>
                    {report.resolved_at && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Resolved At</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(report.resolved_at)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* User Description */}
                {report.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">User Description</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{report.description}</p>
                  </div>
                )}

                {/* Message Context */}
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Conversation Context (Last 10 Messages)</label>
                  <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4 bg-gray-50">
                    {messages.length === 0 ? (
                      <p className="text-sm text-gray-500">No messages found in context</p>
                    ) : (
                      messages.map((msg, index) => {
                        const isUser = msg.role === 'user'
                        return (
                          <div
                            key={index}
                            className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-md rounded-lg p-3 ${
                                isUser
                                  ? 'bg-red-600 text-white'
                                  : 'bg-white text-gray-900 border border-gray-200'
                              }`}
                            >
                              <div className="flex items-center mb-1">
                                {isUser ? (
                                  <User className="h-4 w-4 mr-1" />
                                ) : (
                                  <Bot className="h-4 w-4 mr-1" />
                                )}
                                <span className="text-xs font-medium">
                                  {isUser ? 'User' : 'AI Assistant'}
                                </span>
                                {msg.createdAt && (
                                  <span className={`ml-auto text-xs ${isUser ? 'text-red-100' : 'text-gray-500'}`}>
                                    {formatDate(msg.createdAt)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>

                {/* Status Update */}
                {report.status === 'open' && (
                  <div className="border-t border-gray-200 pt-4">
                    <label className="text-sm font-medium text-gray-500 mb-2 block">Update Status</label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleStatusChange('resolved')}
                        className="btn btn-success btn-sm"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark as Resolved
                      </button>
                      <button
                        onClick={() => handleStatusChange('ignored')}
                        className="btn btn-info btn-sm"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Ignore Report
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Status Update Confirmation Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowUpdateModal(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Update Report Status
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to mark this report as <strong>{selectedStatus}</strong>?
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowUpdateModal(false)
                    }}
                    className="btn btn-secondary btn-sm"
                    disabled={updateStatusMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmStatusUpdate}
                    className={`btn btn-sm ${
                      selectedStatus === 'resolved' ? 'btn-success' : 'btn-info'
                    }`}
                    disabled={updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending ? 'Updating...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

